"use client";

import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";

import PATHS from "@/routes/paths";
import { Form, FormLabel } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Spinner } from "@/components/ui/spinner";

const VERIFICATION_CODE_LENGTH = 6;

interface ParentVerificationFormProps {
  goToPrevStep: () => void;
}

const ParentVerificationForm = ({ goToPrevStep }: ParentVerificationFormProps) => {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();

  const verificationSchema = z.object({
    verificationCode: z.string().length(VERIFICATION_CODE_LENGTH, { message: "" }),
  });

  type VerificationValues = z.infer<typeof verificationSchema>;

  const form = useForm<VerificationValues>({
    resolver: zodResolver(verificationSchema),
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const onSubmit: SubmitHandler<VerificationValues> = async ({ verificationCode }) => {
    if (verificationCode.length === VERIFICATION_CODE_LENGTH) {
      // Dry run: do not call API, just navigate
      navigate(PATHS.REDIRECT);
    }
  };

  const shakeAnimation = {
    x: [0, -10, 10, -10, 10, 0],
    transition: { duration: 0.4 },
  };

  return (
    <Form {...form}>
      <form className="flex w-full flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <motion.div
          animate={{ opacity: 1, x: 0 }}
          className="flex w-full justify-center gap-5 overflow-hidden"
          initial={{ opacity: 0, x: -100 }}
        >
          <Controller
            control={control}
            name="verificationCode"
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <div className="relative">
                  <FormLabel className="text-muted-foreground mb-4 block text-center text-sm font-medium">
                    {t("auth:signUp.parentConsent.verification")}
                  </FormLabel>
                  <motion.div animate={error ? shakeAnimation : {}}>
                    <InputOTP
                      containerClassName="mx-auto w-full"
                      disabled={isSubmitting}
                      maxLength={VERIFICATION_CODE_LENGTH}
                      value={value}
                      onChange={onChange}
                      onComplete={() => {
                        // Immediately submit when full code is entered
                        handleSubmit(onSubmit)();
                      }}
                    >
                      {Array.from({ length: VERIFICATION_CODE_LENGTH }, (_, index) => (
                        <InputOTPGroup key={index} className="grow">
                          <InputOTPSlot
                            className={`bg-muted/20 border-border/50 hover:bg-muted/30 focus:bg-muted/40 h-14 w-12 grow text-2xl transition-all duration-200`}
                            index={index}
                          />
                        </InputOTPGroup>
                      ))}
                    </InputOTP>
                  </motion.div>
                  {isSubmitting ? (
                    <div className="bg-muted/30 absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center rounded-xl">
                      <Spinner className="bg-primary" size="md" />
                    </div>
                  ) : null}
                </div>
              );
            }}
          />
        </motion.div>
        <div className="mt-4 flex w-full justify-start">
          <Button
            className={
              "bg-muted/20 hover:bg-muted/30 border-border/50 w-20 shadow-lg backdrop-blur-sm"
            }
            disabled={isSubmitting}
            size="lg"
            type="button"
            variant="outline"
            onClick={goToPrevStep}
          >
            <Icon icon={"mdi:arrow-left"} />
            {t("common:back")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ParentVerificationForm;
